import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import amxLogo from "@/assets/amx-logo-light.svg";
import { portfolioCompanies } from "@/data/portfolioCompanies";

const HeroAlt = () => {
  // Ensure Sirocco shows prominently in the marquee
  const base = [...portfolioCompanies];
  const sirocco = base.find((c) => c.id === "sirocco");
  const others = base.filter((c) => c.id !== "sirocco");
  const heroCompanies = sirocco ? [sirocco, ...others] : base;

  // Duplicate the array for seamless infinite scroll
  const duplicatedCompanies = [...heroCompanies, ...heroCompanies];

  return (
    <section className="min-h-screen flex items-center justify-center px-4 py-20 bg-background">
      <div className="container mx-auto max-w-6xl">
        <div className="space-y-12">
          {/* Brand */}
          <div className="space-y-6">
            <img src={amxLogo} alt="AMX Ventures" className="h-20 md:h-32 w-auto" />
          </div>

          {/* Main message */}
          <div className="space-y-8 max-w-3xl">
            <h1 className="text-5xl md:text-7xl font-black leading-tight">
              <span className="bg-gradient-to-r from-accent to-accent/70 bg-clip-text text-transparent">
                Capital + Channels
              </span>
              <br />
              <span className="text-primary">= Growth</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed max-w-2xl border-l-4 border-accent pl-6">
              Whether you are a <span className="font-bold text-primary">founder</span> scaling through partnerships, a <span className="font-bold text-primary">strategic partner</span> seeking collaboration, or a <span className="font-bold text-primary">customer</span> exploring innovative solutions—we connect ecosystems that drive growth.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button 
                size="lg" 
                variant="default" 
                className="text-lg h-14 px-10 font-bold"
                asChild
              >
                <a href="/schedule">
                  Connect With Us
                  <ArrowRight className="ml-2" />
                </a>
              </Button>
              <Button size="lg" variant="outline" className="text-lg h-14 px-10 font-bold border-2" asChild>
                <a href="/portfolio">
                  Explore Portfolio
                </a>
              </Button>
            </div>
          </div>

          {/* Portfolio Logos */}
          <div className="pt-12 space-y-8">
            <div className="text-center">
              <div className="inline-block px-6 py-3 bg-primary/10 border-2 border-primary/20 rounded">
                <span className="text-sm font-bold tracking-[0.3em] text-primary uppercase">Portfolio</span>
              </div>
              <h3 className="text-3xl md:text-4xl font-black text-foreground mt-4">
                Trusted by Visionaries
              </h3>
            </div>

            <div className="relative overflow-hidden">
              {/* Gradient overlays for fade effect */}
              <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-background to-transparent z-10" />
              <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-background to-transparent z-10" />
              
              {/* Scrolling container */}
              <div className="flex animate-scroll">
                {duplicatedCompanies.map((company, index) => (
                  <div
                    key={`${company.id}-${index}`}
                    className="flex-shrink-0 mx-8 flex items-center justify-center"
                    style={{ width: '200px', height: '100px' }}
                  >
                    <img
                      src={company.logo}
                      alt={`${company.name} logo`}
                      loading="lazy"
                      className="max-w-full max-h-full object-contain filter grayscale hover:grayscale-0 transition-all duration-300 opacity-60 hover:opacity-100"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Key points */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-12">
            <div className="p-8 bg-secondary border-l-4 border-accent">
              <div className="text-5xl font-black text-accent mb-2">10+</div>
              <p className="text-sm font-bold uppercase tracking-wider text-foreground">Years Experience</p>
            </div>
            <div className="p-8 bg-secondary border-l-4 border-primary">
              <div className="text-5xl font-black text-primary mb-2">90%</div>
              <p className="text-sm font-bold uppercase tracking-wider text-foreground">CAC Reduction</p>
            </div>
            <div className="p-8 bg-secondary border-l-4 border-accent">
              <div className="text-5xl font-black text-accent mb-2">6+</div>
              <p className="text-sm font-bold uppercase tracking-wider text-foreground">Portfolio Companies</p>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default HeroAlt;
